import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { UiButtonComponent } from '../../atoms/button/button.component';

export interface UiSignaturePadValue {
  dataUrl: string;
  isEmpty: boolean;
}

let uiSignaturePadUid = 0;

/**
 * Molecula: area de dibujo para recabar una firma manuscrita.
 *
 * Presentacional: emite la firma como data URL, sin persistir ni validar
 * documentos. La feature consumidora decide como usar la imagen resultante.
 */
@Component({
  selector: 'ui-signature-pad',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiButtonComponent],
  templateUrl: './signature-pad.component.html',
  styleUrl: './signature-pad.component.scss',
  host: {
    class: 'ui-signature-pad-host',
    '[class.ui-signature-pad-host--block]': 'block()',
  },
})
export class UiSignaturePadComponent implements OnDestroy {
  readonly surfaceRef = viewChild<ElementRef<HTMLElement>>('surface');
  readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('signatureCanvas');

  readonly label = input('Firma');
  readonly description = input('Dibuja tu firma dentro del recuadro.');
  readonly clearLabel = input('Limpiar');
  readonly placeholder = input('Firma aqui');
  readonly strokeColor = input('#17233c');
  readonly disabled = input(false);
  readonly required = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showMessage = input(true);
  readonly block = input(false);
  readonly fieldId = input<string | undefined>(undefined);

  readonly signatureChanged = output<UiSignaturePadValue>();
  readonly signatureCleared = output<void>();

  protected readonly generatedId = `ui-signature-pad-${++uiSignaturePadUid}`;
  protected readonly canvasId = computed(() => this.fieldId() ?? this.generatedId);
  protected readonly descriptionId = computed(() => `${this.canvasId()}-description`);
  protected readonly messageId = computed(() => `${this.canvasId()}-message`);
  protected readonly isEmpty = signal(true);

  protected readonly ariaDescribedBy = computed(() => {
    const parts: string[] = [];
    if (this.description()) {
      parts.push(this.descriptionId());
    }
    if (this.message() && this.invalid() && this.showMessage()) {
      parts.push(this.messageId());
    }
    return parts.length ? parts.join(' ') : null;
  });

  private context: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  private activePointerId: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeFrameId: number | null = null;
  private lastPoint: { x: number; y: number } | null = null;

  constructor() {
    afterNextRender(() => {
      this.context = this.canvasRef()?.nativeElement.getContext('2d') ?? null;
      this.scheduleResize();
      this.setupResizeObserver();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.resizeFrameId !== null) {
      cancelAnimationFrame(this.resizeFrameId);
    }
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.scheduleResize();
  }

  protected startDrawing(event: PointerEvent): void {
    if (this.disabled() || event.button !== 0) {
      return;
    }

    const canvas = this.canvasRef()?.nativeElement;
    const context = this.getContext();
    if (!canvas || !context) {
      return;
    }

    event.preventDefault();
    canvas.setPointerCapture(event.pointerId);
    this.activePointerId = event.pointerId;
    this.isDrawing = true;
    this.lastPoint = this.getCanvasPoint(event);
  }

  protected draw(event: PointerEvent): void {
    if (
      !this.isDrawing ||
      this.disabled() ||
      this.activePointerId !== event.pointerId ||
      !this.lastPoint
    ) {
      return;
    }

    const context = this.getContext();
    if (!context) {
      return;
    }

    event.preventDefault();
    const nextPoint = this.getCanvasPoint(event);
    context.beginPath();
    context.moveTo(this.lastPoint.x, this.lastPoint.y);
    context.lineTo(nextPoint.x, nextPoint.y);
    context.strokeStyle = this.strokeColor();
    context.lineWidth = 2.4;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.stroke();
    this.lastPoint = nextPoint;

    if (this.isEmpty()) {
      this.isEmpty.set(false);
      this.emitSignature();
    }
  }

  protected stopDrawing(event: PointerEvent): void {
    if (!this.isDrawing || this.activePointerId !== event.pointerId) {
      return;
    }

    this.isDrawing = false;
    this.activePointerId = null;
    this.lastPoint = null;

    const canvas = this.canvasRef()?.nativeElement;
    if (canvas) {
      try {
        if (canvas.hasPointerCapture(event.pointerId)) {
          canvas.releasePointerCapture(event.pointerId);
        }
      } catch {
        // Ignorar si el navegador ya liberó el capture.
      }
    }

    this.emitSignature();
  }

  protected clearSignature(): void {
    if (this.disabled()) {
      return;
    }

    const canvas = this.canvasRef()?.nativeElement;
    const context = this.getContext();
    if (!canvas || !context) {
      return;
    }

    context.clearRect(0, 0, canvas.width, canvas.height);
    this.isEmpty.set(true);
    this.signatureCleared.emit();
    this.signatureChanged.emit({ dataUrl: '', isEmpty: true });
  }

  private setupResizeObserver(): void {
    const surface = this.surfaceRef()?.nativeElement;
    if (!surface || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.scheduleResize());
    this.resizeObserver.observe(surface);
  }

  private scheduleResize(): void {
    if (this.resizeFrameId !== null) {
      cancelAnimationFrame(this.resizeFrameId);
    }

    this.resizeFrameId = requestAnimationFrame(() => {
      this.resizeFrameId = null;
      this.resizeCanvas();
    });
  }

  private resizeCanvas(): void {
    const canvas = this.canvasRef()?.nativeElement;
    const surface = this.surfaceRef()?.nativeElement;
    const context = this.getContext();
    if (!canvas || !surface || !context) {
      return;
    }

    const rect = surface.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) {
      return;
    }

    const ratio = window.devicePixelRatio || 1;
    const nextWidth = Math.max(1, Math.round(rect.width * ratio));
    const nextHeight = Math.max(1, Math.round(rect.height * ratio));

    if (canvas.width === nextWidth && canvas.height === nextHeight) {
      return;
    }

    const currentImage = this.isEmpty() ? null : canvas.toDataURL('image/png');

    canvas.width = nextWidth;
    canvas.height = nextHeight;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = 2.4;
    context.strokeStyle = this.strokeColor();

    if (currentImage) {
      const image = new Image();
      image.onload = () => {
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.drawImage(image, 0, 0, rect.width, rect.height);
      };
      image.src = currentImage;
    }
  }

  private getContext(): CanvasRenderingContext2D | null {
    if (this.context) {
      return this.context;
    }

    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) {
      return null;
    }

    this.context = canvas.getContext('2d');
    return this.context;
  }

  private getCanvasPoint(event: PointerEvent): { x: number; y: number } {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) {
      return { x: 0, y: 0 };
    }

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  private emitSignature(): void {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) {
      return;
    }

    const dataUrl = this.isEmpty() ? '' : canvas.toDataURL('image/png');
    this.signatureChanged.emit({ dataUrl, isEmpty: this.isEmpty() });
  }
}
